import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CompanyMemberRow {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  member_role: string | null;
  member_status: string | null;
  profile_status: string | null;
  suspended_by_company: boolean;
  joined_at: string | null;
}

const fetchCompanyMembers = async (companyId: string): Promise<CompanyMemberRow[]> => {
  const { data: members, error: mErr } = await supabase
    .from("company_members")
    .select("user_id, role, status, suspended_by_company, joined_at")
    .eq("company_id", companyId);
  if (mErr) throw new Error(mErr.message);

  const userIds = Array.from(new Set((members ?? []).map((m) => m.user_id).filter(Boolean))) as string[];
  if (userIds.length === 0) return [];

  const { data: profiles, error: pErr } = await supabase
    .from("profiles")
    .select("id, first_name, last_name, email, status")
    .in("id", userIds);
  if (pErr) throw new Error(pErr.message);

  const profileMap = new Map((profiles ?? []).map((p) => [p.id, p]));

  return (members ?? []).map((m) => {
    const p = profileMap.get(m.user_id);
    return {
      user_id: m.user_id,
      first_name: p?.first_name ?? null,
      last_name: p?.last_name ?? null,
      email: p?.email ?? null,
      member_role: m.role ?? null,
      member_status: m.status ?? null,
      profile_status: p?.status ?? null,
      suspended_by_company: !!m.suspended_by_company,
      joined_at: m.joined_at ?? null,
    };
  });
};

export const useCompanyMembers = (companyId: string | null) => {
  const query = useQuery({
    queryKey: ["company-members", companyId],
    queryFn: () => fetchCompanyMembers(companyId as string),
    enabled: !!companyId,
    staleTime: 30_000,
  });

  return {
    members: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  };
};
